import { useRef } from 'react'
import { usePrevious } from 'react-use'
import useResizeObserver from '@react-hook/resize-observer'
import isNumber from 'lodash/isNumber'

import { cn } from '@/lib/cn'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'

import { AppWrapper } from '../__project__/AppWrapper'
import { BuilderActionMenu } from '../__project__/BuilderActionMenu'
import { BuilderDialog, BuilderGalleryDialog } from '../__project__/BuilderDialog'
import { BuilderTopBar } from './components/BuilderTopBar'
import { DeviceFrame } from './components/DeviceFrame'
import { PreviewFrame } from './components/PreviewFrame'
import { RenderOverlay } from './components/RenderOverlay'
import { RenderPage } from './components/RenderPage'
import { KeyboardShortcutsTip } from './KeyboardShortcutsTip'
import { ToggleMode } from './ToggleMode'

/**
 * App Builder Preview
 */
export default function AppPreview({ isFullScreen }: { isFullScreen: boolean }) {
  const { deviceScale, setDeviceScale, isFitScreen, isDevelopmentMode, setIsDevelopmentMode } = useAppBuilderContext()

  let previousScale = usePrevious(deviceScale)

  const divRef = useRef<HTMLDivElement>(null)
  const deviceRef = useRef<HTMLDivElement>(null)

  useResizeObserver(divRef, (entry) => {
    const { contentRect } = entry

    if (divRef.current && deviceRef.current && isFitScreen) {
      const parentRect = contentRect
      const deviceHeight = deviceRef.current.getBoundingClientRect().height / deviceScale

      const scaleFactor = (parentRect.height * 0.96) / deviceHeight

      let updatedScale = Number(scaleFactor.toFixed(2))

      if (updatedScale > 1 && isNumber(previousScale)) {
        updatedScale = previousScale
      }

      // Set the scale state variable
      setDeviceScale(updatedScale)
    }
  })

  const { isTabletLandscape, isDesktop, isTV, isSticky, isNavPositionLeading, isNavigationBarBottom } = useBreakpoint()
  const isShowNavbar = isSticky || isNavPositionLeading
  const isLargeDevice = isTabletLandscape || isDesktop || isTV

  return (
    <div className='mid-content bg-zinc-50 dark:bg-slate-600 basis-[100%] relative'>
      <div
        ref={divRef}
        id='mk-builder-preview'
        className={cn(
          'h-full relative flex flex-col items-center justify-start',
          isFitScreen ? 'fit-screen relative flex items-start justify-center' : 'flex justify-center overflow-y-auto'
        )}
      >
        <KeyboardShortcutsTip />
        <ToggleMode
          isDevelopmentMode={isDevelopmentMode}
          setIsDevelopmentMode={setIsDevelopmentMode}
          isFullScreen={isFullScreen}
        />

        <DeviceFrame ref={deviceRef} isFullScreen={isFullScreen}>
          <PreviewFrame>
            <AppWrapper>
              {isShowNavbar || (isNavigationBarBottom && isLargeDevice && !isSticky) ? <BuilderTopBar /> : null}
              <RenderPage />
              <BuilderDialog />
              <BuilderActionMenu />
              <BuilderGalleryDialog />
              <RenderOverlay />
            </AppWrapper>
          </PreviewFrame>
        </DeviceFrame>
      </div>
    </div>
  )
}
